import { FC, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import API from 'core/Api';
import styled from 'styled-components';
import { useActiveClientContext } from 'store/active-client';
import { buildClientEntity } from 'utils/helpers/client';
import { useParams } from 'react-router';
import { ISelectOptionProps } from 'components/form/types';
import { useLanguageContext } from 'store/language';
import InnerWrapper from '../../InnerWrapper';
import { IEmployee } from '../types';
import EditForm from './components/EditForm';
import UpcomingLeaves from './components/UpcomingLeaves';
import { IDropdowns } from '../components/types';

const Screen: FC = () => {
  const { id } = useParams<{ id: string | undefined }>();
  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();
  const { t } = useTranslation();
  const [isSpecialView, setIsSpecialView] = useState(false);
  const {
    isLoading: isLoadingUser,
    error: errorUser,
    data: user,
  } = useQuery<IEmployee, Error>(
    [buildClientEntity('user', client), { id }],
    () =>
      API.get<{ items: [IEmployee] | [] }>(`users?id=${id}`).then(res => {
        if (res.data && res.data.items.length === 0) throw new Error(t('common.notExistingUser'));
        if (res.data && res.data.items.length !== 0 && !res.data.items[0].can_edit)
          throw new Error(t('common.noPermission'));
        return {
          ...(res.data.items.length ? res.data?.items[0].user_permissions : null),
          ...(res.data.items.length ? res.data?.items[0].user_data : null),
          ...(res.data.items.length ? res.data.items[0] : null),
          user_data: null,
          user_permissions: null,
          has_hidden_sensitive_fields: res.data.items.length ? res.data.items[0].has_hidden_sensitive_fields : false,
        };
      }),
    {
      retry: false,
    }
  );

  const {
    isLoading: isLoadingUserDropdowns,
    error: errorUserDropdowns,
    data: dropdowns,
  } = useQuery<IDropdowns, Error>([buildClientEntity('user-dropdowns', client)], (): Promise<IDropdowns> => {
    return API.get('users/dropdown-options').then(res => res.data && res.data[language || 'bg']);
  });

  const {
    isLoading: isLoadingRoles,
    error: errorRoles,
    data: roles,
  } = useQuery<ISelectOptionProps[], Error>([buildClientEntity('roles', client)], (): Promise<ISelectOptionProps[]> => {
    return API.get('roles').then(
      res =>
        res.data &&
        res.data.items.map((role: { [key: string]: string | number }) => ({
          value: role.id,
          label: role.name,
        }))
    );
  });

  return (
    <InnerWrapper
      isLoading={isLoadingUser && isLoadingUserDropdowns && isLoadingRoles}
      error={errorUser || errorUserDropdowns || errorRoles}
    >
      <ResponsiveDiv>
        <EditFormWrapper>
          {!isLoadingUser && user && dropdowns && roles && (
            <EditForm
              user={user}
              roleOptions={roles}
              userDataOptions={dropdowns}
              onSpecialViewChange={setIsSpecialView}
            />
          )}
        </EditFormWrapper>
        {!isSpecialView && <UpcomingLeavesWrapper>{user && <UpcomingLeaves id={user.id} />}</UpcomingLeavesWrapper>}
      </ResponsiveDiv>
    </InnerWrapper>
  );
};

export default Screen;

const ResponsiveDiv = styled.div`
  display: flex;

  @media (max-width: 1199px) {
    flex-direction: column-reverse;
  }
`;

const EditFormWrapper = styled.div`
  width: 100%;
`;

const UpcomingLeavesWrapper = styled.div`
  margin-left: -30%;

  @media (max-width: 1199px) {
    width: 50%;
    margin-left: 16.6666666667%;
  }
  @media (max-width: 575px) {
    width: 100%;
    margin-left: 0;
  }
`;
